import React, { useState, useEffect } from "react";
import {
  Offcanvas,
  Form,
  Button,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import AssignmentService, {
  Assignment,
  AssignmentResource,
  AssignmentQuestionDef,
} from "../../../_services/AssignmentService";
import StudyResourceService from "../../../_services/StudyResourceService";
import ApiService from "../../../_services/ApiService";

interface Props {
  show: boolean;
  onClose: (refresh?: boolean) => void;
  assignment: Assignment | null;
  readOnly?: boolean;
}

const RESPONSE_TYPES = [
  { value: "TEXT", label: "Text Response" },
  { value: "FILE", label: "File Upload" },
  { value: "TEXT_AND_FILE", label: "Text + File Upload" },
  { value: "QUESTIONS", label: "Questions" },
];

const QUESTION_TYPES = [
  { value: "SHORT_ANSWER", label: "Short Answer" },
  { value: "PARAGRAPH", label: "Paragraph" },
  { value: "ESSAY", label: "Essay" },
];

const AssignmentFormModal: React.FC<Props> = ({
  show,
  onClose,
  assignment,
  readOnly,
}) => {
  const isEdit = !!assignment?.Id;

  // Basic fields
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [responseType, setResponseType] = useState<string>("TEXT");
  const [maxScore, setMaxScore] = useState<string>("");
  const [maxAttempts, setMaxAttempts] = useState<number>(1);
  const [allowLate, setAllowLate] = useState(false);
  const [courseId, setCourseId] = useState<number | undefined>();

  // File config
  const [maxFiles, setMaxFiles] = useState(3);
  const [maxFileSize, setMaxFileSize] = useState(10);

  // Resources
  const [resources, setResources] = useState<AssignmentResource[]>([]);
  const [resourceSearch, setResourceSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Questions
  const [questions, setQuestions] = useState<AssignmentQuestionDef[]>([]);

  // Dropdowns
  const [courses, setCourses] = useState<any[]>([]);

  // State
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load form data on edit
  useEffect(() => {
    if (assignment) {
      setTitle(assignment.Title || "");
      setInstructions(assignment.Instructions || "");
      setResponseType(assignment.ResponseType || "TEXT");
      setMaxScore(assignment.MaxScore?.toString() || "");
      setMaxAttempts(assignment.MaxAttempts || 1);
      setAllowLate(!!assignment.AllowLateSubmission);
      setCourseId(assignment.CourseId || undefined);

      if (assignment.Config) {
        setMaxFiles(assignment.Config.maxFiles || 3);
        const rawSize = assignment.Config.maxFileSize;
        setMaxFileSize(rawSize > 100 ? Math.round(rawSize / (1024 * 1024)) : rawSize || 10);
      }

      if (assignment.resources) {
        setResources(
          assignment.resources.map((r: any) => ({
            resourceId: r.resourceId,
            resourceName: r.resourceName,
            resourceTypeName: r.resourceTypeName,
            sortOrder: r.sortOrder || 0,
            label: r.label || "",
          }))
        );
      }

      if (assignment.questions) {
        setQuestions(
          assignment.questions.map((q: any) => ({
            questionText: q.QuestionText || q.questionText || "",
            questionType: q.QuestionType || q.questionType || "SHORT_ANSWER",
            questionId: q.QuestionId || q.questionId || undefined,
            maxScore: q.MaxScore || q.maxScore || undefined,
            sortOrder: q.SortOrder || q.sortOrder || 0,
            required: q.Required !== 0,
            QuestionBankText: q.QuestionBankText,
          }))
        );
      }
    }
  }, [assignment]);

  // Load courses
  useEffect(() => {
    ApiService.getAcademicCourses()
      .then((res: any) => setCourses(res.data?.data || res.data || []))
      .catch(() => {});
  }, []);

  // Search study resources
  const handleResourceSearch = async () => {
    if (!resourceSearch.trim()) return;
    setSearchLoading(true);
    try {
      const res = await StudyResourceService.getStudyResources({
        search: resourceSearch,
        limit: 20,
        offset: 0,
      });
      const results = res.data || [];
      // Filter out already-added resources
      const addedIds = new Set(resources.map((r) => r.resourceId));
      setSearchResults(results.filter((r: any) => !addedIds.has(r.id)));
    } catch {
      setSearchResults([]);
    }
    setSearchLoading(false);
  };

  const addResource = (r: any) => {
    setResources([
      ...resources,
      {
        resourceId: r.id,
        resourceName: r.name,
        resourceTypeName: r.typeName,
        sortOrder: resources.length,
        label: "",
      },
    ]);
    setSearchResults(searchResults.filter((sr: any) => sr.id !== r.id));
  };

  const removeResource = (idx: number) => {
    setResources(resources.filter((_, i) => i !== idx));
  };

  // Question management
  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        questionText: "",
        questionType: "SHORT_ANSWER",
        sortOrder: questions.length,
        required: true,
      },
    ]);
  };

  const updateQuestion = (idx: number, field: string, value: any) => {
    const updated = [...questions];
    (updated[idx] as any)[field] = value;
    setQuestions(updated);
  };

  const removeQuestion = (idx: number) => {
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  // Submit
  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    setSaving(true);
    setError("");

    const payload: any = {
      title,
      instructions,
      responseType,
      maxScore: maxScore ? parseFloat(maxScore) : null,
      maxAttempts,
      allowLateSubmission: allowLate,
      courseId: courseId || null,
      config:
        responseType === "FILE" || responseType === "TEXT_AND_FILE"
          ? { maxFiles, maxFileSize: maxFileSize * 1024 * 1024 }
          : null,
      resources: resources.map((r, i) => ({
        resourceId: r.resourceId,
        sortOrder: i,
        label: r.label || null,
      })),
      questions:
        responseType === "QUESTIONS"
          ? questions.map((q, i) => ({
              questionText: q.questionText || null,
              questionType: q.questionType,
              questionId: q.questionId || null,
              maxScore: q.maxScore || null,
              sortOrder: i,
              required: q.required !== false,
            }))
          : [],
    };

    try {
      if (isEdit) {
        await AssignmentService.update(assignment!.Id!, payload);
      } else {
        await AssignmentService.create(payload);
      }
      onClose(true);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save assignment");
    }
    setSaving(false);
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => onClose()}
      placement="end"
      style={{ width: 600 }}
      className="form-drawer"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {readOnly ? "View Assignment" : isEdit ? "Edit Assignment" : "Create Assignment"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {error && (
          <div className="alert alert-danger py-2 mb-3">{error}</div>
        )}

        {/* Basic Info */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Title *</Form.Label>
          <Form.Control
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Assignment title"
            disabled={readOnly}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Instructions</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Instructions for students..."
            disabled={readOnly}
          />
        </Form.Group>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold">Course</Form.Label>
              <Form.Select
                value={courseId || ""}
                onChange={(e) =>
                  setCourseId(
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                disabled={readOnly}
              >
                <option value="">-- Select --</option>
                {courses.map((c: any) => (
                  <option key={c.Id || c.id || c.courseId} value={c.Id || c.id || c.courseId}>
                    {c.Name || c.name || c.courseName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold">Max Score</Form.Label>
              <Form.Control
                type="number"
                value={maxScore}
                onChange={(e) => setMaxScore(e.target.value)}
                placeholder="e.g. 100"
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Response Type */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Response Type *</Form.Label>
          <div className="d-flex flex-wrap" style={{ gap: '0.75rem 1.5rem' }}>
            {RESPONSE_TYPES.map((rt) => (
              <Form.Check
                key={rt.value}
                type="radio"
                label={rt.label}
                name="responseType"
                checked={responseType === rt.value}
                onChange={() => setResponseType(rt.value)}
                disabled={readOnly}
              />
            ))}
          </div>
        </Form.Group>

        {/* File config */}
        {(responseType === "FILE" || responseType === "TEXT_AND_FILE") && (
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Max Files</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={10}
                  value={maxFiles}
                  onChange={(e) => setMaxFiles(Number(e.target.value))}
                  disabled={readOnly}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Max File Size (MB)</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={50}
                  value={maxFileSize}
                  onChange={(e) => setMaxFileSize(Number(e.target.value))}
                  disabled={readOnly}
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        {/* Questions Builder */}
        {responseType === "QUESTIONS" && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="fw-semibold mb-0">Questions</Form.Label>
              {!readOnly && (
                <Button variant="secondary" size="sm" onClick={addQuestion}>
                  + Add Question
                </Button>
              )}
            </div>

            {questions.length === 0 && (
              <div className="text-muted small">
                No questions added yet. Click "Add Question" to start.
              </div>
            )}

            {questions.map((q, idx) => (
              <div key={idx} className="question-builder-item">
                <div className="d-flex justify-content-between mb-2">
                  <span className="fw-medium">Question {idx + 1}</span>
                  {!readOnly && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeQuestion(idx)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={q.questionText || ""}
                  onChange={(e) =>
                    updateQuestion(idx, "questionText", e.target.value)
                  }
                  placeholder="Enter question text..."
                  className="mb-2"
                />
                <Row>
                  <Col md={6}>
                    <Form.Select
                      size="sm"
                      value={q.questionType}
                      onChange={(e) =>
                        updateQuestion(idx, "questionType", e.target.value)
                      }
                    >
                      {QUESTION_TYPES.map((qt) => (
                        <option key={qt.value} value={qt.value}>
                          {qt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      size="sm"
                      type="number"
                      placeholder="Max score"
                      value={q.maxScore || ""}
                      onChange={(e) =>
                        updateQuestion(
                          idx,
                          "maxScore",
                          e.target.value ? Number(e.target.value) : undefined
                        )
                      }
                    />
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        )}

        {/* Attached Resources */}
        <div className="mb-3">
          <Form.Label className="fw-semibold">
            Attached Resources (PDFs, Videos, etc.)
          </Form.Label>

          {resources.map((r, idx) => (
            <div key={idx} className="resource-picker-item">
              <span className="text-muted small">{r.resourceTypeName}</span>
              <span>{r.resourceName}</span>
              <Form.Control
                size="sm"
                placeholder="Label (optional)"
                value={r.label || ""}
                onChange={(e) => {
                  const updated = [...resources];
                  updated[idx].label = e.target.value;
                  setResources(updated);
                }}
                style={{ maxWidth: 150 }}
              />
              {!readOnly && (
                <span
                  className="remove-btn"
                  onClick={() => removeResource(idx)}
                >
                  x
                </span>
              )}
            </div>
          ))}

          {!readOnly && (
            <>
              <div className="d-flex gap-2 mt-2">
                <Form.Control
                  size="sm"
                  placeholder="Search study resources..."
                  value={resourceSearch}
                  onChange={(e) => setResourceSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleResourceSearch()}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleResourceSearch}
                  disabled={searchLoading}
                >
                  {searchLoading ? <Spinner size="sm" /> : "Search"}
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div
                  className="mt-2"
                  style={{
                    maxHeight: 200,
                    overflowY: "auto",
                    border: "1px solid var(--border-default)",
                    borderRadius: 4,
                  }}
                >
                  {searchResults.map((r: any) => (
                    <div
                      key={r.id}
                      className="d-flex align-items-center justify-content-between px-2 py-1"
                      style={{
                        borderBottom: "1px solid var(--border-subtle)",
                        cursor: "pointer",
                      }}
                      onClick={() => addResource(r)}
                    >
                      <span>
                        <span className="text-muted small me-2">
                          {r.typeName}
                        </span>
                        {r.name}
                      </span>
                      <span style={{ color: "#1976d2" }}>+ Add</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Settings */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Max Attempts</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(Number(e.target.value))}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
          <Col md={6} className="d-flex align-items-end">
            <Form.Check
              type="switch"
              label="Allow Late Submission"
              checked={allowLate}
              onChange={(e) => setAllowLate(e.target.checked)}
              disabled={readOnly}
            />
          </Col>
        </Row>

        {/* Actions */}
        <div className="d-flex justify-content-end gap-2 mt-4">
          {readOnly ? (
            <Button variant="secondary" onClick={() => onClose()}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => onClose()}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                {saving ? (
                  <Spinner size="sm" />
                ) : isEdit ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </>
          )}
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AssignmentFormModal;
